import { AfterViewInit, Component, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import {
  NewReportingFilter,
  NewReportResponse,
  NewReportTrafficDataResponse,
  NewWrappedReportResponse,
  ReportingsCollection,
  SelectionOutput
} from '../shared/interfaces/new-reporting-filter';
import { ReportingService } from '../shared/services/reporting.service';
import { FilterBuilderService } from '../shared/services/filter-builder.service';
import { SpecialCasesService } from '../shared/services/special-cases.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ReportConfig } from '../shared/interfaces/new-reporting-filter';
import { ReportConfigService } from '../shared/interfaces/report-config';
import { AlertService } from 'src/app/shared/alert/shared/alert.service';
import { AlertTypes } from 'src/app/shared/alert/shared/alert-types';
import { UserDataService } from "../../user/shared/services/user-data.service";
import { checkAccessAuthorization } from "../../user/shared/role.guard";
import { Subscription } from 'rxjs';

const today = new Date();
const month = today.getMonth();
const year = today.getFullYear();

@Component({
  selector: 'avs-reporting-main-page',
  standalone: false,
  templateUrl: './reporting-main-page.component.html',
  styleUrl: './reporting-main-page.component.css'
})
export class ReportingMainPageComponent implements OnDestroy {
  @ViewChildren('downloadMenu') downloadMenus!: QueryList<MatMenu>;
  downloadMenuMap = new Map<string, MatMenu>();

  private destroy$ = new Subject<void>();

  // UI State
  public selectedMenu: string = 'Projekte';
  public selectedStatus = 'aktiv';
  public searchFilter: string = '';

  // Loading States
  public loadingReportId: string | null = null;
  public downloadingReportId: string | null = null;

  // Date Range
  public range = new FormGroup({
    start: new FormControl(new Date(year, month - 1, 1)),
    end: new FormControl(new Date(year, month, 0))
  });
  public maxDate: Date = new Date();
  // Data
  public selectionOutput: SelectionOutput | null = null;
  public reportingFilter: NewReportingFilter | null = null;
  public reportCollection: ReportingsCollection[] = [];
  public reportConfigs: ReportConfig[] = [];

  // Special Cases
  public currentReportConfig: ReportConfig | null = null;
  public selectedDeChannels: number[] = [];
  public selectedMinutes: number = 0;
  public selectedIoChannels: Record<string, string> = {};
  public selectedDownloadFormat?: string = undefined;

  // Menu Hover State
  private timedOutCloser: any;
  private closeDelay = 50;
  private currentProbablyOpenMenuTrigger: MatMenuTrigger | null = null;
  private currentProbablyOpenMenuForReport: string | null = null;

  public userDataSubscription?: Subscription;
  public user: any;
  
  constructor(
    private reportingService: ReportingService,
    private reportConfigService: ReportConfigService,
    private filterBuilder: FilterBuilderService,
    private specialCases: SpecialCasesService,
    private alertService: AlertService,
    private userDataService: UserDataService
  ) {
    this.reportConfigs = this.reportConfigService.getReportConfigs();
    this.userDataSubscription = userDataService.userData.subscribe(user => { if (user) { this.user = user; } });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  resetSelection(): void {
    this.selectionOutput = null;  
  }

  /**
   * Clear search filter
   */
  public clearSearch(): void {
    this.searchFilter = '';
  }

  /**
   * Handle selection change
   */
  public selectionChange(event: SelectionOutput): void {
    this.selectionOutput = event;
  }

  public setMaxDate()                                                          
  {
    this.maxDate = new Date();                                // max to current date.... 
  
    let cStartDate = this.range.get('start')?.value;          // additionally: look at start 
    if (cStartDate instanceof Date) {
      let actStartDate = new Date(cStartDate);
      let maxRangeEnd = new Date(actStartDate?.setMonth(actStartDate.getMonth() + 1)); // allow max start + 1 month - 1 day, resulting in range of 1 month
      maxRangeEnd = new Date(maxRangeEnd?.setDate(maxRangeEnd.getDate() - 1));
      if (maxRangeEnd < this.maxDate)                         // take the minimum of 1 month range and now for max
        this.maxDate = maxRangeEnd;
    }
  }

  /**
   * Generate base reporting filter from UI state
   */
  private generateReportingFilter(): void {
    if (!this.selectionOutput || !this.range.value.start || !this.range.value.end) {
      return;
    }

    try {
      this.reportingFilter = this.filterBuilder.buildFilterFromSelection(
        this.selectionOutput,
        new Date(this.range.value.start),
        new Date(this.range.value.end)
      );
    } catch (error) {
    }
  }

  /**
   * Open Special Cases Dialog/Panel für Report
   */
  public openSpecialCases(reportId: string, format?:string): void {
    const config = this.reportConfigService.getReportConfig(reportId);
    if (!config) return;

    this.currentReportConfig = config;

    // Reset special cases
    this.selectedDeChannels = [];
    this.selectedMinutes = config.minutesDefault || 0;
    this.selectedIoChannels = {};
    this.selectedDownloadFormat = format;
  }

  /**
   * Close Special Cases Dialog
   */
  public closeSpecialCases(): void {
    this.currentReportConfig = null;
    this.specialCases.resetSpecialCases();
    this.selectedDownloadFormat = undefined;
  }

  /**
   * Apply Special Cases
   */
  public applySpecialCases(): void {
    if (!this.currentReportConfig) return;

    this.specialCases.updateSpecialCasesData({
      deChannels: this.selectedDeChannels.length > 0 ? this.selectedDeChannels : undefined,
      minutes: this.selectedMinutes,
      ioChannels: Object.keys(this.selectedIoChannels).length > 0 ? this.selectedIoChannels : undefined
    });
     
    /*    console.log('✅ Special cases applied:', {
          deChannels: this.selectedDeChannels,
          minutes: this.selectedMinutes,
          ioChannels: this.selectedIoChannels
        });
    */
    if ( this.selectedDownloadFormat === undefined)
      this.executeGetReport(this.currentReportConfig.id);
    else
      this.executeDownloadReport(this.currentReportConfig.id, this.selectedDownloadFormat);
  
    this.closeSpecialCases();
  }

  /**
   * Toggle deChannel selection
   */
  public toggleDeChannel(channelId: number): void {
    const index = this.selectedDeChannels.indexOf(channelId);
    if (index > -1) {
      this.selectedDeChannels.splice(index, 1);
    } else {
      this.selectedDeChannels.push(channelId);
    }
  }

  /**
   * Add ioChannel
   */
  public addIoChannel(key: string, value: string): void {
    if (key && value) {
      this.selectedIoChannels[key] = value;
    }
  }

  /**
   * Remove ioChannel
   */
  public removeIoChannel(key: string): void {
    delete this.selectedIoChannels[key];
  }

  /**
   * Get Report (display in UI)
   */
  public getReport(reportId: string): void {
    const config = this.reportConfigService.getReportConfig(reportId);
    if (!config) {
      return;
    }

    // Wenn Special Cases nötig → Dialog öffnen
    if (this.specialCases.hasSpecialCases(config)) {
      this.openSpecialCases(reportId);
      return;
    }

    this.executeGetReport(reportId);
  }

  /**
   * Führe GetReport aus (nach Special Cases oder sofort)
   */
  private executeGetReport(reportId: string): void {
    const config = this.reportConfigService.getReportConfig(reportId);
    if (!config) return;

    this.loadingReportId = reportId;

    // 1. Generate base filter
    this.generateReportingFilter();
    if (!this.reportingFilter || !this.selectionOutput) {
      this.loadingReportId = null;
      return;
    }

    // 2. Build report-specific filter
    let reportFilter = this.filterBuilder.buildFilterForReport(
      this.reportingFilter,
      config
    );

    // 3. Merge Special Cases
    reportFilter = this.specialCases.mergeWithFilter(reportFilter, config);

    // 4. Clean undefined values
    const cleanedFilter = this.filterBuilder.cleanFilter(reportFilter);

    // 5. Validate
    const validation = this.filterBuilder.validateFilterForReport(cleanedFilter, config);
    if (!validation.valid) {
      console.error(
        `❌ Missing required fields for ${config.label}:`,
        validation.missingFields
      );
      this.loadingReportId = null;
      // TODO: Show error toast
      return;
    }


    // 6. Call service
    config.serviceMethod(cleanedFilter, this.selectionOutput.timezoneId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: NewReportResponse | NewReportTrafficDataResponse[] | NewWrappedReportResponse[]) => {

          if (Object.keys(result).length === 0) {
            console.info(`⚠️ ${config.label} report returned empty result.`);
            this.alertService.alert('Keine Daten für dieses Gerät und/oder diesen Zeitraum vorhanden', AlertTypes.info);
          }

          // Single Report
          else if (!Array.isArray(result) && this.isNewReportResponse(result)) {
            console.log ("Got single report");
            const reportCollectionItem: ReportingsCollection = {
              reportName: `${config.reportType} ${cleanedFilter.samId ? ' Station ' + this.selectionOutput?.name : cleanedFilter.projectId ? ' Projekt ' + this.selectionOutput?.name : ' Gruppe ' + this.selectionOutput?.name}`,
              reportFromUTC: cleanedFilter.utcFrom,
              reportToUTC: cleanedFilter.utcTo,
              reportType: config.reportType,
              groupId: cleanedFilter.groupId,
              projectId: cleanedFilter.projectId,
              samId: cleanedFilter.samId,
              reportData: result
            };
            this.reportCollection.push(reportCollectionItem);
          }

          // Array-Fälle
          else if (Array.isArray(result) && result.length > 0) {
            console.log ("Got array report.  " );

            // TrafficData-Reports
            if (this.isNewReportTrafficDataResponse(result[0])) {
              console.log('array result detected - traffic data');
              result.forEach(item => {
                if (this.isNewReportTrafficDataResponse(item)) {
                  const reportCollectionItem: ReportingsCollection = {
                    reportName: `${config.reportType} Station ${item.deviceName}: ${item.lane}`,
                    reportFromUTC: cleanedFilter.utcFrom,
                    reportToUTC: cleanedFilter.utcTo,
                    reportType: config.reportType,
                    groupId: cleanedFilter.groupId,
                    projectId: cleanedFilter.projectId,
                    samId: cleanedFilter.samId,
                    reportData: item.data
                  };
                  this.reportCollection.push(reportCollectionItem);
                }
              });
            }
          
          
          // Wrapped-Reports
            else if (this.isNewWrappedReportResponse(result[0])) {
            console.log ("Got wrapped report");
              console.log('array result detected - wrapped');
              result.forEach(item => {
                if (this.isNewWrappedReportResponse(item)) {
                  const reportCollectionItem: ReportingsCollection = {
                    reportName: `${config.reportType} Station ${this.selectionOutput?.name}`,
                    reportFromUTC: cleanedFilter.utcFrom,
                    reportToUTC: cleanedFilter.utcTo,
                    reportType: config.reportType,
                    groupId: cleanedFilter.groupId,
                    projectId: cleanedFilter.projectId,
                    samId: item.samId,
                    reportData: item.data
                  };
                  this.reportCollection.push(reportCollectionItem);
                }
              });
            }
            else {
              console.error(`❌ Unexpected report format for ${config.label}:`, result);
              this.alertService.alert('Unerwartetes Berichtsformat empfangen', AlertTypes.error);
            }
          }
          console.log(`✅ ${config.label} report received:`, result);
          this.loadingReportId = null;
        },
        error: (error) => {
          console.error(`❌ Error fetching ${config.label} report:`, error);
          this.loadingReportId = null;
        }
      });

  }

  // Type guard for NewReportResponse
  private isNewReportResponse(value: any): value is NewReportResponse {
    return (
      value &&
      typeof value === 'object' &&
      Array.isArray(value.labels) &&
      Array.isArray(value.content) &&
      typeof value.timezoneId === 'string'
    );
  }

  private isNewReportTrafficDataResponse(value: any): value is NewReportTrafficDataResponse/*  | NewWrappedReportResponse */ {    /* Oder einfach nach dem wrapped model fragen? */
    return (
      value &&
      typeof value === 'object' &&
      (value.deviceName === null || typeof value.deviceName === 'string') &&
      (value.lineDescription === null || typeof value.lineDescription === 'string') &&
      typeof value.channel === 'number' &&
      typeof value.lane === 'string' &&
      typeof value.samId === 'number' &&
      value.data &&
      typeof value.data === 'object'
    );
  }


  private isNewWrappedReportResponse(value: any): value is NewWrappedReportResponse { /* zerschossen :D */
    return (
      value &&
      typeof value === 'object' &&
      value.data &&
      typeof value.data === 'object'
    );
  }

  /**
   * Download Report
   */
  public downloadReport(reportId: string, format: string): void {
    const config = this.reportConfigService.getReportConfig(reportId);
    if (!config) {
      console.warn(`❌ Report config "${reportId}" not found`);
      return;
    }

    // Wenn Special Cases nötig → Dialog öffnen
    if (this.specialCases.hasSpecialCases(config)) {
      this.openSpecialCases(reportId, format);
      return;
    }

    this.executeDownloadReport(reportId, format);
  }

  /**
   * Führe Download aus (nach Special Cases oder sofort)
   */
  private executeDownloadReport(reportId: string, format: string): void {
    const config = this.reportConfigService.getReportConfig(reportId);
    if (!config) return;

    this.downloadingReportId = reportId;

    // 1. Generate base filter
    this.generateReportingFilter();
    if (!this.reportingFilter || !this.selectionOutput) {
      console.error('❌ Cannot download - missing filter or selection');
      this.downloadingReportId = null;
      return;
    }

    // 2. Build report-specific filter
    let reportFilter = this.filterBuilder.buildFilterForReport(
      this.reportingFilter,
      config
    );

    // 3. Merge Special Cases
    reportFilter = this.specialCases.mergeWithFilter(reportFilter, config);

    // 4. Clean undefined values
    const cleanedFilter = this.filterBuilder.cleanFilter(reportFilter);

    // 5. Validate
    const validation = this.filterBuilder.validateFilterForReport(cleanedFilter, config);
    if (!validation.valid) {
      console.error(
        `❌ Missing required fields for ${config.label}:`,
        validation.missingFields
      );
      this.downloadingReportId = null;
      return;
    }
    /* 
        console.log(`⬇️ Downloading ${config.label} as ${format}:`, cleanedFilter); */

    // 6. Call download service
    config.downloadMethod(cleanedFilter, this.selectionOutput.timezoneId, format)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          console.log(`Got blob for id ${this.downloadingReportId} downloaded as ${format}`);
          console.log(blob);
          if (blob.size < 3) {
            console.info(`⚠️ ${config.label} report download returned empty result.`);
            this.alertService.alert('Keine Daten für dieses Gerät und/oder diesen Zeitraum vorhanden', AlertTypes.info);
          }
          else {
            this.downloadBlob(blob, config.reportType, format);
            console.log(`✅ ${config.label} downloaded as ${format}`);
          }
          this.downloadingReportId = null;
        },
        error: (error) => {
           console.error(`❌ Error downloading ${config.label}:`, error);
           this.alertService.alert('Fehler beim Download der Berichtsdaten', AlertTypes.error);
           this.downloadingReportId = null;
        }
      });
  }

  /**
   * Save blob as file
   */
  private downloadBlob(blob: Blob, reportType: string, format: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    console.log('downloadBlob ${url}');

    link.download = `${reportType.replace(/\s+/g, '')}Report.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Menu hover handler - close open menue with entering button for different report
   */
  public mouseEnter(trigger: MatMenuTrigger, report: string): void {
    if (this.currentProbablyOpenMenuForReport && this.currentProbablyOpenMenuForReport !== report) {
      this.currentProbablyOpenMenuTrigger?.closeMenu();
    }
    this.currentProbablyOpenMenuForReport = report;
    this.currentProbablyOpenMenuTrigger = trigger;
  }

  /**
   * Menu hover handler - close
   */
  /*public mouseLeave(trigger: MatMenuTrigger, engine: string): void {
    this.timedOutCloser = setTimeout(() => {
      trigger.closeMenu();
      if (this.currentOpenEngine === engine) {
        this.currentOpenEngine = null;
        this.currentOpenTrigger = null;
      }
    }, this.closeDelay);
    console.log("mouse leave: "+ engine)
  }*/

  /**
   * Überprüfe ob Report Special Cases hat
   */
  public hasSpecialCases(config: ReportConfig): boolean {
    return this.specialCases.hasSpecialCases(config);
  }

  public isReportDisabled(config: ReportConfig): boolean {
    return (!this.selectionOutput || this.isDownloadingReport(config.id) || !this.isReportAvailable(config));
  }
  
  /**
   * Check if Report is currently loading
   */
  public isLoadingReport(reportId: string): boolean {
    return this.loadingReportId === reportId;
  }

  /**
   * Check if Report is currently downloading
   */
  public isDownloadingReport(reportId: string): boolean {
    return this.downloadingReportId === reportId;
  }

  public isReportAvailable(config: ReportConfig): boolean {
    // Wenn availableFor nicht definiert → immer verfügbar
    if (!this.selectionOutput || !config.availableFor || config.availableFor.length === 0) {
      return true;
    }

    if (config.availableFor.includes(this.selectionOutput.type)) {
      return true;
    }
    return false;

  }

  public isUserAuthorizedForReport(config: ReportConfig): boolean {

    let access = checkAccessAuthorization(config.userRoleAccess, this.user.role);
    return access;
  }
  /**
   * Get visible reports (gefiltert nach Verfügbarkeit)
   */
  public getVisibleReports(): ReportConfig[] {
    return this.reportConfigs.filter(config => this.isUserAuthorizedForReport(config) && this.isReportAvailable(config));
  }
    public getReports(): ReportConfig[] {
    return this.reportConfigs.filter(config => this.isUserAuthorizedForReport(config));
  }

}


